/**
 * Container Button Link Component
 * A special arrangement for links similar to bootstrap-4's custom input pattern
 * - A hidden link interacts with the keyboard
 * - A sibling container displays styles based on the state of the hidden link
 * 
 * Use this in situations where a clickable area may contain other interactable elements
 * Satisfies the accessibility guideline that links should not contain other interactive widgets
 * 
 * Ported from: online-community/Assets/projects/ocfe/src/buttons/ContainerButtonLink.tsx
 */

import React, { Ref, useCallback, useRef } from 'react';

/**
 * Check if an element is interactable (button, link, input, etc.)
 */
const isInteractable = (element: HTMLElement): boolean => {
  const tagName = element.tagName.toLowerCase();
  const interactableTags = ['a', 'button', 'input', 'select', 'textarea'];
  
  if (interactableTags.includes(tagName)) {
    return true;
  }
  
  // Check for role attribute that makes it interactive
  const role = element.getAttribute('role');
  if (role && ['button', 'link', 'tab', 'checkbox', 'radio'].includes(role)) {
    return true;
  }
  
  return false;
};

/**
 * Get all elements between target and container
 */
const getElementsBetween = (target: HTMLElement, container: HTMLElement): HTMLElement[] => {
  const elements: HTMLElement[] = [];
  let current: HTMLElement | null = target;
  
  while (current && current !== container) {
    elements.push(current);
    current = current.parentElement;
  }
  
  return elements;
};

export type ContainerButtonProps = {
  contentRef?: Ref<HTMLDivElement>;
  contentClassName?: string;
  className?: string;
  style?: React.CSSProperties;
  children?: React.ReactNode;
  href?: string;
  onClick?: React.MouseEventHandler<HTMLButtonElement>;
  'aria-label'?: string;
  'qa-id'?: string;
};

/**
 * ContainerButtonLink - supports both href (link) and onClick (button) modes
 * When href is provided, renders as a link. Otherwise, renders as a button.
 */
const ContainerButton: React.FC<ContainerButtonProps> = ({
  className = '',
  contentRef,
  contentClassName = '',
  children,
  href,
  onClick,
  style,
  'aria-label': ariaLabel,
  'qa-id': qaId,
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const linkRef = useRef<HTMLAnchorElement>(null);
  const btnRef = useRef<HTMLButtonElement>(null);

  const safeClick = useCallback(
    (e: React.MouseEvent) => {
      if (!containerRef.current) {
        return;
      }

      if (isInteractable(e.target as HTMLElement)) {
        return;
      }

      const elementsBetweem = getElementsBetween(e.target as HTMLElement, containerRef.current);
      if (elementsBetweem.some(isInteractable)) {
        return;
      }

      if (href) {
        linkRef.current?.click();
      } else if (onClick) {
        onClick(e as React.MouseEvent<HTMLButtonElement>);
      }
    },
    [href, onClick]
  );

  return (
    // eslint-disable-next-line jsx-a11y/no-static-element-interactions, jsx-a11y/click-events-have-key-events
    <div className={`ai-container-btn ${className}`} ref={containerRef} style={style} onClick={safeClick}>
      {href ? (
        <a 
          ref={linkRef} 
          className="sr-only ai-container-btn-button" 
          href={href}
          aria-label={ariaLabel}
          data-qa-id={qaId}
          target="_blank"
          rel="noopener noreferrer"
          hidden={true}
        >
          {' '}
        </a>
      ) : (
        <button 
          ref={btnRef} 
          className="sr-only ai-container-btn-button" 
          type="button" 
          onClick={onClick}
          aria-label={ariaLabel}
          data-qa-id={qaId}
        />
      )}
      <div className={`ai-container-btn-content ${contentClassName}`} style={{ height: '100%' }} ref={contentRef}>
        {children}
      </div>
    </div>
  );
};

export default React.memo(ContainerButton);
